import type { PendoIdentity } from '@klareospace/pharos-lib-front-utils';

// --- Pendo agent snippet types ---

const PENDO_AGENT_SCRIPT_URL_PREFIX = 'https://cdn.pendo.io/agent/static';
const PENDO_QUEUE_METHODS: ('identify' | 'initialize' | 'clearSession')[] = ['initialize', 'identify', 'clearSession'];

interface PendoVisitor {
  id: string;
  role?: string;
}

interface PendoAccount {
  id: string;
}

interface PendoInitializeOptions {
  account?: PendoAccount;
  visitor: PendoVisitor;
}

type PendoAgentMethod = (...args: unknown[]) => void;

interface PendoAgent {
  _q?: unknown[][];
  clearSession?: PendoAgentMethod;
  identify?: PendoAgentMethod;
  initialize?: PendoAgentMethod;
}

declare global {
  interface Window {
    pendo?: PendoAgent;
  }
}

// --- Public API ---

export interface PendoClientConfig {
  apiKey: string;
}

export interface PendoClient {
  clearSession: () => void;
  identify: (identity: PendoIdentity) => void;
}

const NO_OP_CLIENT: PendoClient = {
  clearSession: (): void => {},
  identify: (): void => {},
};

/**
 * Create a Pendo web agent client.
 *
 * When `apiKey` is empty (develop/staging), a no-op client is returned — no script is
 * injected and no network call to pendo.io ever happens. With a key, the official install
 * snippet is injected lazily on the first `identify` call: it defines queuing stubs on
 * `window.pendo` so `initialize` is buffered and replayed once the async agent script
 * loads. Subsequent identify calls use `pendo.identify`. Every method is
 * try/catch-swallowed — Pendo must never cause an app error.
 */
export function createPendoClient(config: PendoClientConfig): PendoClient {
  if (!config.apiKey) {
    return NO_OP_CLIENT;
  }

  let isAgentInjected = false;
  let hasIdentified = false;

  return {
    clearSession: (): void => {
      try {
        if (isAgentInjected) {
          window.pendo?.clearSession?.();
        }
      } catch (error) {
        // Pendo must never cause an app error — warn so the failure is diagnosable.
        console.warn('[pendo] clearSession failed', error);
      }
    },

    identify: (identity: PendoIdentity): void => {
      try {
        if (!isAgentInjected) {
          injectPendoAgent(config.apiKey);
          isAgentInjected = true;
        }

        const options = toPendoInitializeOptions(identity);
        if (hasIdentified) {
          window.pendo?.identify?.(options);
        } else {
          window.pendo?.initialize?.(options);
          hasIdentified = true;
        }
      } catch (error) {
        // Pendo must never cause an app error — warn so the failure is diagnosable.
        console.warn('[pendo] identify failed', error);
      }
    },
  };
}

// --- Helpers ---

/**
 * Injects the official Pendo install snippet: defines queuing stubs on `window.pendo` so
 * `initialize`/`identify`/`clearSession` calls made before the async agent script loads are
 * buffered and replayed by the real agent once loaded (mirrors the official Pendo loader,
 * which queues `clearSession` too).
 */
function injectPendoAgent(apiKey: string): void {
  const pendo: PendoAgent = window.pendo ?? {};
  pendo._q = pendo._q ?? [];

  PENDO_QUEUE_METHODS.forEach((method): void => {
    if (pendo[method]) return;
    pendo[method] = (...args: unknown[]): void => {
      pendo._q?.[method === 'initialize' ? 'unshift' : 'push']([method, ...args]);
    };
  });
  window.pendo = pendo;

  const script = document.createElement('script');
  script.async = true;
  script.src = `${PENDO_AGENT_SCRIPT_URL_PREFIX}/${apiKey}/pendo.js`;
  document.head.appendChild(script);
}

function toPendoInitializeOptions(identity: PendoIdentity): PendoInitializeOptions {
  return {
    ...(identity.tenantId && { account: { id: identity.tenantId } }),
    visitor: { id: identity.userId, ...(identity.role && { role: identity.role }) },
  };
}
